import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import Dropzone from 'react-dropzone';
import { toast } from 'react-toastify';
import { EmvioButton as Button, EmvioValidatedInput, EmvioValidatedForm } from '@nexio/emvio-form-components';
import { EmvioLoadingSpinner as LoadingSpinner } from '@nexio/emvio-util-ui';
import Modal from '../../Modal/Modal';
import { toggleModal } from '../../Modal/modalActions';
import { getDataByUniqueModalName, getIsOpenByUniqueModalName } from '../../Modal/modalViewState';
import { createApplication, resetCreateApplication } from '../createApplicationActions';
import {
    getIsCreatingApplication,
    getCreateApplicationError,
    getCreateApplicationSuccess,
    getCreateApplicationId,
    getOnboardingId
} from '../createApplicationEntity';
import { getMerchantDetails } from '../../MerchantDetailsPage/merchantDetailsActions';
import './CreateApplicationModal.scss';

const MAX_FILE_SIZE_LIMIT = 5242880; // 5 MB
const MAX_FILES_LIMIT = 10;

export class CreateApplicationModal extends Component {
    static propTypes = {
        isOpen: PropTypes.bool,
        onToggleModal: PropTypes.func.isRequired,
        rowData: PropTypes.object,
        data: PropTypes.object,
        isCreating: PropTypes.bool,
        error: PropTypes.object,
        createSuccess: PropTypes.bool,
        applicationId: PropTypes.string,
        onboardingId: PropTypes.number,
        onCreateApplication: PropTypes.func.isRequired,
        onResetCreateApplication: PropTypes.func.isRequired,
        onRefreshMerchantDetails: PropTypes.func.isRequired,
        history: PropTypes.object.isRequired,
        location: PropTypes.object.isRequired
    };

    static defaultProps = {
        isOpen: false,
        data: null
    };

    constructor(props) {
        super(props);
        const isNewApplication = props.data?.isNewApplication || false;
        this.state = {
            view: isNewApplication ? 'businessName' : 'question', // 'businessName', 'question', or 'upload'
            hasDocument: null,
            supportingDocuments: [],
            businessName: ''
        };
        this.dropzoneRef = React.createRef();
    }

    componentDidUpdate(prevProps) {
        const modalJustOpened = !prevProps.isOpen && this.props.isOpen;
        const dataChanged = prevProps.data !== this.props.data;
        const isOpen = this.props.isOpen;

        if (isOpen && (modalJustOpened || dataChanged)) {
            const isNewApplication = this.props.data?.isNewApplication || false;
            const correctView = isNewApplication ? 'businessName' : 'question';

            this.setState({
                view: correctView,
                hasDocument: null,
                supportingDocuments: [],
                businessName: ''
            });

            this.props.onResetCreateApplication();
        }

        // Handle successful application creation and navigate to merchant detail page
        if (!prevProps.createSuccess && this.props.createSuccess) {
            const onboardingId = this.props.onboardingId;
            if (onboardingId) {
                this.props.onToggleModal('createApplicationModal');
                this.props.onResetCreateApplication();

                setTimeout(() => {
                    const currentPath = this.props.location.pathname;
                    const targetPath = `/merchantdetails/${onboardingId}`;

                    if (currentPath === targetPath) {
                        this.props.onRefreshMerchantDetails(onboardingId);
                    } else {
                        this.props.history.push(targetPath);
                    }
                }, 100);
            } else {
                toast.error('Application created but onboarding ID not found');
            }
        }

        if (!prevProps.error && this.props.error) {
            const errorMessage = typeof this.props.error === 'string'
                ? this.props.error
                : this.props.error?.message || 'Failed to create application. Please try again.';
            toast.error(errorMessage);
        }
    }

    handleModalOpen = () => {
        const isNewApplication = this.props.data?.isNewApplication || false;
        this.setState({
            view: isNewApplication ? 'businessName' : 'question',
            hasDocument: null,
            supportingDocuments: [],
            businessName: ''
        });
    };

    handleBusinessNameNext = (e) => {
        if (e) {
            e.preventDefault();
        }
        if (!this.state.businessName || this.state.businessName.trim() === '') {
            toast.error('Please enter a business name');

            return;
        }
        this.setState({
            view: 'question'
        });
    };

    handleBusinessNameChange = (e) => {
        this.setState({
            businessName: e.target.value
        });
    };

    // Create new applicaiton with no additional documents
    handleNo = () => {
        const formData = new FormData();
        const isNewApplication = this.props.data?.isNewApplication || false;
        const prevetId = this.props.data?.prevetId;

        if (isNewApplication) {
            // New Application flow: send businessName only (no files)
            if (!this.state.businessName || this.state.businessName.trim() === '') {
                toast.error('Please enter a business name');

                return;
            }
            formData.append('businessName', this.state.businessName.trim());
        } else {
            // Start Application flow from Prevet: send prevetId only (no new documents)
            if (!prevetId) {
                toast.error('Prevet ID not found');

                return;
            }
            formData.append('prevetId', prevetId.toString());
        }

        this.props.onCreateApplication(formData);
    };

    handleYes = () => {
        this.setState({
            view: 'upload',
            hasDocument: true
        });
    };

    handleClose = () => {
        this.props.onToggleModal('createApplicationModal');
    };

    onDrop = (acceptedFiles, rejectedFiles) => {
        if (rejectedFiles.length > 0) {
            toast.error('Please ensure files are PDF format only.');

            return;
        }

        const currentFileCount = this.state.supportingDocuments.length;
        const totalFilesAfterAdd = currentFileCount + acceptedFiles.length;

        if (totalFilesAfterAdd > MAX_FILES_LIMIT) {
            const allowedFiles = MAX_FILES_LIMIT - currentFileCount;
            if (allowedFiles > 0) {
                acceptedFiles = acceptedFiles.slice(0, allowedFiles);
            } else {
                toast.error(`Maximum ${MAX_FILES_LIMIT} files allowed. Please remove some files before adding new ones.`);

                return;
            }
        }

        const oversizedFile = acceptedFiles.find((file) => file.size > MAX_FILE_SIZE_LIMIT);
        if (oversizedFile) {
            toast.error(`File "${oversizedFile.name}" exceeds the 5MB limit. Total size for all files cannot exceed 5MB.`);

            return;
        }

        const currentTotalSize = this.state.supportingDocuments.reduce((sum, file) => sum + file.size, 0);

        const newFilesSize = acceptedFiles.reduce((sum, file) => sum + file.size, 0);

        if (currentTotalSize + newFilesSize > MAX_FILE_SIZE_LIMIT) {
            toast.error('Total size for all files cannot exceed 5MB.');

            return;
        }

        this.setState((prevState) => ({
            supportingDocuments: [...prevState.supportingDocuments, ...acceptedFiles]
        }));
    };

    onOpenFileDialog = () => {
        if (this.dropzoneRef.current) {
            this.dropzoneRef.current.open();
        }
    };

    removeFile = (index) => {
        this.setState((prevState) => ({
            supportingDocuments: prevState.supportingDocuments.filter((_, i) => i !== index)
        }));
    };

    // Create new applicaiton with addtional documents
    handleStartApplication = () => {
        if (this.state.supportingDocuments.length === 0) {
            toast.error('Please upload at least one document');

            return;
        }

        const formData = new FormData();
        const isNewApplication = this.props.data?.isNewApplication || false;
        const prevetId = this.props.data?.prevetId;

        if (isNewApplication) {
            // New Application flow: send businessName + files
            if (!this.state.businessName || this.state.businessName.trim() === '') {
                toast.error('Please enter a business name');

                return;
            }
            formData.append('businessName', this.state.businessName.trim());
        } else {
            // Start Application flow: send prevetId + new files
            if (!prevetId) {
                toast.error('Prevet ID not found');

                return;
            }
            formData.append('prevetId', prevetId.toString());
        }

        this.state.supportingDocuments.forEach((file) => {
            formData.append('files', file, file.name);
        });

        this.props.onCreateApplication(formData);
    };

    getModalTitle = () => {
        const isNewApplication = this.props.data?.isNewApplication || false;

        return isNewApplication ? 'New Application' : 'Start Application';
    };

    getButtonText = () => {
        const isNewApplication = this.props.data?.isNewApplication || false;

        return isNewApplication ? 'New Application' : 'Start Application';
    };

    renderBusinessNameView = () => {
        const { businessName } = this.state;

        return (
            <div className="create-application-modal-business-name">
                <div className="business-name-content">
                    <div className="business-name-description">
                        Please enter the Merchant&apos;s legal business name to get started.
                    </div>
                    <EmvioValidatedForm onSubmit={this.handleBusinessNameNext} className="business-name-form">
                        <EmvioValidatedInput
                            labelText="LEGAL BUSINESS NAME"
                            name="businessName"
                            onChange={this.handleBusinessNameChange}
                            placeholder="Legal Business Name"
                            type="text"
                            validations={['required']}
                            value={businessName}
                            shouldNotTrim={true}
                        />
                        <div className="business-name-actions">
                            <Button
                                className="business-name-button"
                                variant="primary"
                                type="submit"
                                disabled={this.props.isCreating}
                                isLoading={this.props.isCreating}
                            >
                                Next
                            </Button>
                        </div>
                    </EmvioValidatedForm>
                </div>
            </div>
        );
    };

    renderQuestionView = () => {
        return (
            <div className="create-application-modal-question">
                <div className="question-content">
                    <div className="question-text">
                        Do you have a document to upload to pre-fill the app with?
                    </div>
                    <div className="question-buttons">
                        <Button
                            className="question-button"
                            variant="secondary"
                            onClick={this.handleNo}
                            disabled={this.props.isCreating}
                        >
                            No
                        </Button>
                        <Button
                            className="question-button"
                            variant="primary"
                            onClick={this.handleYes}
                            disabled={this.props.isCreating}
                        >
                            Yes
                        </Button>
                    </div>
                </div>
            </div>
        );
    };

    renderLoadingView = () => {
        return (
            <div className="loading-view">
                <div className="loading-content">
                    <div className="loading-spinner">
                        <LoadingSpinner />
                    </div>
                    <p className="loading-message">
                        Creating application. This may take a few moments...
                    </p>
                </div>
            </div>
        );
    };

    renderUploadView = () => {
        const { supportingDocuments } = this.state;

        return (
            <div className="create-application-modal-upload">
                <div className="upload-content">
                    <div className="upload-section">
                        <div className="field-label">
                            Upload Document here
                        </div>
                        <Dropzone
                            accept="application/pdf"
                            className="file-upload-container"
                            disableClick
                            multiple
                            onDrop={this.onDrop}
                            ref={this.dropzoneRef}
                        >
                            <div className="file-upload-content">
                                <div className="file-upload-text">
                                    Drag files here to upload
                                </div>
                                <div className="file-upload-or">
                                    or
                                </div>
                                <Button
                                    className="file-upload-button"
                                    type="button"
                                    onClick={this.onOpenFileDialog}
                                >
                                    Browse File
                                </Button>
                            </div>
                        </Dropzone>
                        <div className="file-upload-caption">
                            Accepted file formats: PDF
                        </div>

                        {supportingDocuments.length > 0 && (
                            <div className="uploaded-files">
                                {supportingDocuments.map((file, index) => (
                                    <div key={`${file.name}-${file.size}`} className="uploaded-file">
                                        <span className="file-name">{file.name}</span>
                                        <button
                                            type="button"
                                            className="remove-file"
                                            onClick={() => this.removeFile(index)}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="upload-actions">
                        <Button
                            className="start-application-button"
                            type="button"
                            onClick={this.handleStartApplication}
                            disabled={this.props.isCreating}
                            isLoading={this.props.isCreating}
                        >
                            {this.getButtonText()}
                        </Button>
                    </div>
                </div>
            </div>
        );
    };

    render() {
        const { view } = this.state;
        const { isCreating } = this.props;

        return (
            <Modal
                uniqueModalName="createApplicationModal"
                title={this.getModalTitle()}
                onOpen={this.handleModalOpen}
                className="create-application-modal"
                forceOpen={isCreating}
            >
                <div className="create-application-modal-content">
                    {isCreating ? (
                        this.renderLoadingView()
                    ) : (
                        <>
                            {view === 'businessName' && this.renderBusinessNameView()}
                            {view === 'question' && this.renderQuestionView()}
                            {view === 'upload' && this.renderUploadView()}
                        </>
                    )}
                </div>
            </Modal>
        );
    }
}

function mapStateToProps(state) {
    return {
        data: getDataByUniqueModalName(state, 'createApplicationModal'),
        isOpen: getIsOpenByUniqueModalName(state, 'createApplicationModal'),
        isCreating: getIsCreatingApplication(state),
        error: getCreateApplicationError(state),
        createSuccess: getCreateApplicationSuccess(state),
        applicationId: getCreateApplicationId(state),
        onboardingId: getOnboardingId(state)
    };
}

function mapDispatchToProps(dispatch) {
    return {
        onToggleModal: (modalName) => dispatch(toggleModal(modalName)),
        onCreateApplication: (formData) => dispatch(createApplication(formData)),
        onResetCreateApplication: () => dispatch(resetCreateApplication()),
        onRefreshMerchantDetails: (onboardingId) => dispatch(getMerchantDetails(onboardingId))
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CreateApplicationModal));
